import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Candidate|History|Fulfillment|Queue|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.CredentialFulfillment,
	portal: 'SP',
};

type LocNameStart = 'SPCandidateFulfillmentQueue';
export type SPCandidateFulfillmentQueueLocNames =
	| `${LocNameStart}TitleSection`
	| `${LocNameStart}NotificationPendingFulfillment`
	| `${LocNameStart}LabelExpedite`
	| `${LocNameStart}LabelTracking`
	| `${LocNameStart}ContentInfo`
	| `${LocNameStart}ButtonCancel`
	| `${LocNameStart}ButtonQueue`
	| `${LocNameStart}NotificationSelectFulfillment`
	| `${LocNameStart}TitleCredentialBased`
	| `${LocNameStart}TitleGeneralFulfillment`
	| `${LocNameStart}LabelName`
	| `${LocNameStart}LabelDescription`
	| `${LocNameStart}LabelQueueType`
	| `${LocNameStart}LabelFulfillmentType`
	| `${LocNameStart}LabelFilterFulfillmentType`
	| `${LocNameStart}NotificationQueueError`
	| `${LocNameStart}NotificationQueueSuccess`;

export const spCandidateFulfillmentQueueLocs: LocDefs<SPCandidateFulfillmentQueueLocNames> = {
	SPCandidateFulfillmentQueueTitleSection: {
		defaultValue: 'Queue Fulfillment',
		description: 'Section Title',
		displayPath: `${pathStart}Section Title`,
		displayType: 'Title',
		legacyName: 'Credential.Fulfillment/Candidates.FulfillmentQueue.Title.Queue',
		...sharedProperties,
	},
	SPCandidateFulfillmentQueueNotificationPendingFulfillment: {
		defaultValue:
			"A fulfillment is pending. Before a fulfillment can be queued, the candidate's address must be updated/confirmed.",
		description: 'Notification for pending fulfillment',
		displayPath: `${pathStart}Pending Fulfillment Notification`,
		displayType: 'Notification',
		legacyName: 'Credential.Fulfillment/Candidates.Notification.Content.PendingFulfillment',
		...sharedProperties,
	},
	SPCandidateFulfillmentQueueLabelExpedite: {
		defaultValue: 'Expedite',
		description: 'Label for expedite field',
		displayPath: `${pathStart}Expedite`,
		displayType: 'Label',
		legacyName: 'Credential.Fulfillment/Candidates.FulfillmentQueue.FieldLabel.Expedite',
		...sharedProperties,
	},
	SPCandidateFulfillmentQueueLabelTracking: {
		defaultValue: 'Tracking',
		description: 'Label for tracking field',
		displayPath: `${pathStart}Tracking`,
		displayType: 'Label',
		legacyName: 'Credential.Fulfillment/Candidates.FulfillmentQueue.FieldLabel.Tracking',
		...sharedProperties,
	},
	SPCandidateFulfillmentQueueContentInfo: {
		defaultValue:
			'The expedite and tracking requests are passed along to the fulfillment house. It does not affect export timing from CertMetrics.',
		description: 'Content for fulfillment info',
		displayPath: `${pathStart}Content Info`,
		displayType: 'Content',
		legacyName: 'Credential.Fulfillment/Candidates.FulfillmentQueue.Content.Info',
		...sharedProperties,
	},
	SPCandidateFulfillmentQueueButtonCancel: {
		defaultValue: 'Cancel',
		description: 'Cancel button text',
		displayPath: `${pathStart}Cancel`,
		displayType: 'Button',
		legacyName: 'Credential.Fulfillment/Candidates.FulfillmentQueue.Button.Cancel',
		...sharedProperties,
	},
	SPCandidateFulfillmentQueueButtonQueue: {
		defaultValue: 'Queue Fulfillment',
		description: 'Queue button text',
		displayPath: `${pathStart}Queue`,
		displayType: 'Button',
		legacyName: 'Credential.Fulfillment/Candidates.FulfillmentQueue.Button.Queue',
		...sharedProperties,
	},
	SPCandidateFulfillmentQueueNotificationSelectFulfillment: {
		defaultValue: 'You must select a fulfillment',
		description: 'Error message when no fulfillment is selected',
		displayPath: `${pathStart}Select Fulfillment Error`,
		displayType: 'Error',
		legacyName:
			'Credential.Fulfillment/Candidates.FulfillmentQueue.Notification.SelectFulfillment',
		...sharedProperties,
	},
	SPCandidateFulfillmentQueueTitleCredentialBased: {
		defaultValue: 'Credential based fulfillment',
		description: 'Title for credential based fulfillment',
		displayPath: `${pathStart}Select Fulfillment Error`,
		displayType: 'Title',
		legacyName: 'Credential.Fulfillment/Candidates.FulfillmentQueue.Title.CredentialBased',
		...sharedProperties,
	},
	SPCandidateFulfillmentQueueTitleGeneralFulfillment: {
		defaultValue: 'General fulfillment',
		description: 'Title for general fulfillment',
		displayPath: `${pathStart}Select Fulfillment Error`,
		displayType: 'Title',
		legacyName: 'Credential.Fulfillment/Candidates.FulfillmentQueue.Title.GeneralFulfillment',
		...sharedProperties,
	},
	SPCandidateFulfillmentQueueLabelDescription: {
		defaultValue: 'Description',
		description: 'Column heading for: Description',
		displayPath: `${pathStart}Description`,
		displayType: 'Label',
		legacyName: 'Credential.Fulfillment/Candidates.FulfillmentQueue.ColumnHeading.Description',
		...sharedProperties,
	},
	SPCandidateFulfillmentQueueLabelName: {
		defaultValue: 'Name',
		description: 'Column heading for: Name',
		displayPath: `${pathStart}Name`,
		displayType: 'Label',
		legacyName: 'Credential.Fulfillment/Candidates.FulfillmentQueue.ColumnHeading.Name',
		...sharedProperties,
	},
	SPCandidateFulfillmentQueueLabelQueueType: {
		defaultValue: 'Queue Type',
		description: 'Column heading for: Queue Type',
		displayPath: `${pathStart}Queue Type`,
		displayType: 'Label',
		legacyName: 'Credential.Fulfillment/Candidates.FulfillmentQueue.ColumnHeading.QueueType',
		...sharedProperties,
	},
	SPCandidateFulfillmentQueueLabelFulfillmentType: {
		defaultValue: 'Fulfillment Type',
		description: 'Column heading for: Fulfillment Type',
		displayPath: `${pathStart}Fulfillment Type`,
		displayType: 'Label',
		legacyName:
			'Credential.Fulfillment/Candidates.FulfillmentQueue.ColumnHeading.FulfillmentType',
		...sharedProperties,
	},
	SPCandidateFulfillmentQueueLabelFilterFulfillmentType: {
		defaultValue: 'Filter by Fulfillment Type',
		description: 'Label for filter by fulfillment type',
		displayPath: `${pathStart}Filter Fulfillment Type`,
		displayType: 'Label',
		legacyName:
			'Credential.Fulfillment/Candidates.FulfillmentQueue.FieldLabel.FilterFulfillmentType',
		...sharedProperties,
	},
	SPCandidateFulfillmentQueueNotificationQueueError: {
		defaultValue: 'There was an error queuing:',
		description: 'Notification for queue error',
		displayPath: `${pathStart}Queue Error`,
		displayType: 'Notification',
		legacyName: 'Credential.Fulfillment/Candidates.FulfillmentQueue.Notification.QueueError',
		...sharedProperties,
	},
	SPCandidateFulfillmentQueueNotificationQueueSuccess: {
		defaultValue: 'Fulfillments successfully queued',
		description: 'Notification for successful queue',
		displayPath: `${pathStart}Queue Success`,
		displayType: 'Notification',
		legacyName: 'Credential.Fulfillment/Candidates.FulfillmentQueue.Notification.QueueSuccess',
		...sharedProperties,
	},
};

export type SPCandidateFulfillmentQueueLocType = LocMap<SPCandidateFulfillmentQueueLocNames>;
